import { useState, useEffect, useRef, useMemo, FC } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import debounce from 'lodash/debounce';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ADDRESS_FRAGMENT } from '../../fragments/address';
import { useStoreSelector } from '../../hooks/useStoreFromContext';
import { findCapitalCoordinates } from '../../lib/countries/countries.utils';
import { useToast } from '../../context/ToastContext';

import type { AppState } from '../../store';
import type { AddressTypes } from '../../types/addressTypes';

type SearchAddressQuery = {
  searchAddress: AddressTypes[];
};

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN;

const SEARCH_ADDRESS = gql`
  query SearchAddress($query: String!) {
    searchAddress(query: $query) { ...AddressFragment }
  }
  ${ADDRESS_FRAGMENT}
`;

type AddressPickerProps = {
  name: string;
};

const AddressPicker: FC<AddressPickerProps> = ({ name }) => {
  const user = useStoreSelector((s: AppState) => s.user);
  const toast = useToast();
  const { control, setValue, formState: { errors } } = useFormContext();
  const error = errors[name];
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [fetchLocations, { loading, data }] = useLazyQuery<SearchAddressQuery>(SEARCH_ADDRESS);
  const suggestions = data?.searchAddress || [];

  useEffect(() => {
    if (map.current) return;
    
    const currentVal = control._defaultValues[name];
    const coords = currentVal?.longitude 
        ? [currentVal.longitude, currentVal.latitude] 
        : findCapitalCoordinates(user?.countryCode);

    map.current = new mapboxgl.Map({
      container: mapContainer.current!,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: coords as [number, number],
      zoom: 17,
    });

    if (currentVal?.longitude) {
      marker.current = new mapboxgl.Marker({ color: '#3b82f6' })
        .setLngLat(coords as [number, number])
        .addTo(map.current!);
    }
  }, [user?.countryCode, name, control]);

  const debouncedSearch = useMemo(
    () => debounce((val: string) => {
      if (val.length >= 3) {
        fetchLocations({ variables: { query: val } });
        setShowDropdown(true);
      } else {
        setShowDropdown(false);
      }
    }, 500),
    [fetchLocations]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelect = (loc: any) => {
    const coords: [number, number] = [loc.longitude, loc.latitude];

    if (!loc.house || !loc.mapboxId) {
      return toast.error('Please select a valid address (full address is required)');
    }
    setValue(name, loc, { shouldValidate: true, shouldDirty: true });
    setShowDropdown(false);

    map.current?.flyTo({ center: coords, zoom: 17 });

    if (marker.current) {
      marker.current.setLngLat(coords);
    } else {
      marker.current = new mapboxgl.Marker({ color: '#3b82f6' })
        .setLngLat(coords)
        .addTo(map.current!);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <Controller
          name={name}
          control={control}
          rules={{ required: "Address is required. Please select from the list." }}
          render={({ field }) => (
            <>
              <input
                type="text"
                autoComplete="off"
                value={field.value?.fullAddress || field.value || ''}
                className={`w-full p-3 border rounded-lg transition-all outline-none
                  ${error ? "border-red-500" : "border-gray-300 dark:border-gray-700"} 
                  dark:bg-gray-900 dark:text-gray-100`}
                placeholder="Start typing address..."
                onChange={(e) => {
                  field.onChange(e.target.value);
                  debouncedSearch(e.target.value);
                }}
              />
              {loading && <div className="absolute right-3 top-3 animate-spin text-blue-500">🌀</div>}
            </>
          )}
        />

        {showDropdown && suggestions.length > 0 && (
          <ul className="absolute z-50 w-full bg-white border dark:border-gray-600 rounded-lg mt-1 max-h-60 overflow-y-auto">
            {suggestions.map((loc: AddressTypes) => (
              <li
                key={loc.id}
                onClick={() => handleSelect(loc)}
                className="p-3 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-none 
                  dark:bg-gray-900 dark:text-white dark:hover:bg-gray-700 dark:border-gray-600"
              >
                {loc.fullAddress}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && typeof error.message === 'string' && <span className="text-sm text-red-500">{error.message}</span>}

      <div
        ref={mapContainer}
        className="w-full h-[300px] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
      />
    </div>
  );
};

export default AddressPicker;
